"use client";

import { createContext, useContext, useEffect, useCallback, useRef, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useUiMode } from "@/components/ui-mode";
import {
  trackEvent as rawTrackEvent,
  identifyUser,
  clearIdentityLocal,
  sanitizePath,
  type AnalyticsEvent,
} from "@/lib/analytics";

interface AnalyticsContextValue {
  trackEvent: (event: AnalyticsEvent) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return ctx;
}

interface AnalyticsProviderProps {
  userId: string | null;
  children: ReactNode;
}

export function AnalyticsProvider({ userId, children }: AnalyticsProviderProps) {
  const { mode } = useUiMode();
  const pathname = usePathname();
  const prevModeRef = useRef(mode);

  // Identify user when authenticated
  useEffect(() => {
    if (userId) {
      identifyUser(userId);
    }
  }, [userId]);

  // Track mode changes (fires mode_toggle event)
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      rawTrackEvent(
        { name: "mode_toggle", from_mode: prevModeRef.current, to_mode: mode },
        {
          ui_mode: prevModeRef.current,
          user_authenticated: !!userId,
          page_path: sanitizePath(pathname),
        },
      );
      prevModeRef.current = mode;
    }
  }, [mode, userId, pathname]);

  // Multi-tab identity sync listener — uses clearIdentityLocal to avoid infinite recursion
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const bc = new BroadcastChannel("analytics-identity-clear");
    const handler = () => clearIdentityLocal();
    bc.addEventListener("message", handler);
    return () => {
      bc.removeEventListener("message", handler);
      bc.close();
    };
  }, []);

  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      rawTrackEvent(event, {
        ui_mode: mode,
        user_authenticated: !!userId,
        page_path: sanitizePath(pathname),
      });
    },
    [mode, userId, pathname],
  );

  return (
    <AnalyticsContext.Provider value={{ trackEvent }}>
      {children}
    </AnalyticsContext.Provider>
  );
}
